// kfe-playlist-row.js — one row of the Global Playlist queue (Brief 090 §2).
//
// PORT TARGET (iOS): the KFELessonCell rows of GlobalPlaylistViewController.m
// (cellForRowAtIndexPath). iOS shows a plain title cell with optional
// description; reorder + delete happen in a table-wide edit mode. The web port
// surfaces the affordances PER ROW (design-doc §9): a drag handle, a play
// toggle, the title, and a remove button — or, in the "Removed" section, a
// single Restore button (§5c).
//
// Presentational + dumb: it renders from props and emits intent events
// (bubbling + composed) that <kfe-global-playlist> handles. It never reaches
// for services or the player directly — that keeps it unit-testable in
// isolation and keeps all queue mutation in one place (the screen).
//
// Events (all composed, via KFEComponent.emit):
//   • "play"     { index }  — play affordance / title tapped
//   • "remove"   { index }  — remove (soft-delete) tapped (active rows only)
//   • "restore"  { index }  — restore tapped (removed rows only)
//   • "row-grab" { index, pointerId, clientY } — drag handle pressed; the
//                 screen takes over pointer tracking for the reorder.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";

export class KFEPlaylistRow extends KFEComponent {
  // `active` = this is the player's current track (highlight); `playing` = audio
  // is currently running (drives the ▶/⏸ glyph); `removed` = render the
  // soft-deleted variant (title + Restore only).
  static props = ["index", "title", "active", "playing", "removed"];

  static styles = `
    :host {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.6rem 0.5rem;
      border-bottom: 1px solid var(--kfe-border);
      color: var(--kfe-fg);
    }
    :host(.active) { background: color-mix(in srgb, var(--kfe-accent) 12%, transparent); }
    :host(.dragging) {
      background: var(--kfe-bg-header);
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.35);
      border-radius: 8px; border-bottom-color: transparent;
    }
    button {
      background: transparent; border: 0; color: inherit; cursor: pointer;
      -webkit-tap-highlight-color: transparent; padding: 0;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .handle {
      flex: none; width: 2rem; align-self: stretch;
      color: var(--kfe-muted); font-size: 1.2rem; line-height: 1;
      cursor: grab; touch-action: none; -webkit-user-select: none; user-select: none;
    }
    .play { flex: none; width: 2.25rem; height: 2.25rem; font-size: 1.2rem;
            border-radius: 50%; color: var(--kfe-accent); }
    .title {
      flex: 1; min-width: 0; text-align: left; font-size: 0.95rem;
      line-height: 1.35;
      /* Long titles WRAP, never truncate (design-doc §7.2 precedent). */
      overflow-wrap: anywhere; word-break: break-word; white-space: normal;
    }
    button.title { cursor: pointer; }
    .remove, .restore { flex: none; padding: 0.25rem 0.5rem; font-size: 1.1rem; }
    .restore {
      font-size: 0.85rem; font-weight: 600; color: var(--kfe-accent);
      border: 1px solid var(--kfe-accent); border-radius: 999px;
    }
  `;

  _idx() { return Number(/** @type {any} */ (this).index) || 0; }

  _onPlay = () => this.emit("play", { index: this._idx() });
  _onRemove = (/** @type {Event} */ e) => { e.stopPropagation(); this.emit("remove", { index: this._idx() }); };
  _onRestore = (/** @type {Event} */ e) => { e.stopPropagation(); this.emit("restore", { index: this._idx() }); };

  /** @param {PointerEvent} e */
  _onGrab = (e) => {
    // Only a primary press starts a drag; let other buttons get their clicks.
    if (!e.isPrimary || e.button !== 0) return;
    e.preventDefault();
    this.emit("row-grab", { index: this._idx(), pointerId: e.pointerId, clientY: e.clientY });
  };

  render() {
    const self = /** @type {any} */ (this);
    const title = self.title || "";
    // Reflect the current-track highlight as a class (not an observed attribute —
    // `active` is a reactive prop, so an attribute toggle would loop back through
    // attributeChangedCallback). The `.dragging` class is owned by the screen.
    this.classList.toggle("active", !!self.active && !self.removed);

    if (self.removed) {
      // Removed-section variant: no play, no reorder, no double-delete — just the
      // title and a single Restore action (Brief 090 §5c).
      return html`
        <span class="title">${title}</span>
        <button class="restore" type="button" on:click=${this._onRestore}>${t("globalPlaylist.restore")}</button>
      `;
    }

    const playing = !!self.active && !!self.playing;
    return html`
      <span
        class="handle"
        aria-label="${t("globalPlaylist.reorder")}"
        title="${t("globalPlaylist.reorder")}"
        on:pointerdown=${this._onGrab}
      >⠿</span>
      <button
        class="play"
        type="button"
        aria-label="${playing ? t("globalPlaylist.pause") : t("globalPlaylist.play")}"
        on:click=${this._onPlay}
      >${playing ? "⏸" : "▶"}</button>
      <button class="title" type="button" on:click=${this._onPlay}>${title}</button>
      <button
        class="remove"
        type="button"
        aria-label="${t("globalPlaylist.remove")}"
        title="${t("globalPlaylist.remove")}"
        on:click=${this._onRemove}
      >✕</button>
    `;
  }
}
